import { useModalState } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Button } from "@mui/material";
import { ACCOUNTS_DOCUMENTS_FULL_PATH } from "@src/appV2/Accounts/Documents/paths";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  BookabilityDecision,
  BookabilityUnmetCriteria,
} from "@src/appV2/OpenShifts/ShiftAction/types";
import { useHistory, useRouteMatch } from "react-router-dom";

import { type ShiftBlockWithMappedDetails } from "../api/useFetchPaginatedShiftBlocks";
import { useShiftBlockBookability } from "../api/useShiftBlockBookability";
import { BookShiftBlockErrors, SHIFT_BLOCK_ACTIONS } from "../utils";
import { BookShiftBlockDialog } from "./BookShiftBlockDialog";

interface BookShiftBlockButtonProps {
  facilityId: string;
  agentId: string;
  shiftBlockId: string;
  shifts: ShiftBlockWithMappedDetails["shifts"];
  onBookingShiftBlock: () => void;
}

export function BookShiftBlockButton(props: BookShiftBlockButtonProps) {
  const { facilityId, agentId, shiftBlockId, shifts, onBookingShiftBlock } = props;
  const { showErrorToast } = useToast();
  const bookShiftBlockState = useModalState();
  const history = useHistory();
  const { path: currentRouterUrl } = useRouteMatch();
  const {
    data: shiftBookabilityData,
    isSuccess: isShiftBookabilitySuccess,
    isLoading: isShiftBookabilityLoading,
  } = useShiftBlockBookability(
    {
      facilityId,
      agentId,
      shiftIds: shifts.map((shift) => shift.id),
      returnFirstUnmetCriteria: false,
    },
    {
      enabled: shifts.length > 0,
    }
  );

  const shiftWithBlockedStatus = shiftBookabilityData?.data?.find(
    (item) => item.attributes.bookability.decision === BookabilityDecision.BLOCKED
  );

  if (isShiftBookabilityLoading) {
    return null;
  }

  if (
    isDefined(shiftWithBlockedStatus) &&
    shiftWithBlockedStatus.attributes.bookability.decision === BookabilityDecision.BLOCKED &&
    shiftWithBlockedStatus.attributes.bookability.unmetCriteria.includes(
      BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS
    )
  ) {
    return (
      <Button
        fullWidth
        variant="contained"
        size="medium"
        onClick={() => {
          logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
            action: SHIFT_BLOCK_ACTIONS.UPLOAD_REQUIRED_DOCUMENTS_TAPPED,
            shiftBlockId,
            facilityId,
            location: currentRouterUrl,
          });
          history.push(ACCOUNTS_DOCUMENTS_FULL_PATH);
        }}
      >
        Upload Required Documents
      </Button>
    );
  }

  return (
    <>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        size="medium"
        disabled={!isShiftBookabilitySuccess}
        onClick={() => {
          if (
            isDefined(shiftWithBlockedStatus) &&
            shiftWithBlockedStatus.attributes.bookability.decision === BookabilityDecision.BLOCKED
          ) {
            logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
              action: SHIFT_BLOCK_ACTIONS.BOOK_BLOCK_TAPPED,
              shiftBlockId,
              facilityId,
              unmetCriteria: shiftWithBlockedStatus.attributes.bookability.unmetCriteria,
              location: currentRouterUrl,
            });
            // There can be multiple reasons for the shift block booking being blocked, however for the purposes of showing the error message to the user, we are only showing the first unmet criteria.
            showErrorToast(
              BookShiftBlockErrors[
                shiftWithBlockedStatus.attributes.bookability.unmetCriteria[0]
              ] ?? "Error: You cannot book this block."
            );
          } else {
            logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
              action: SHIFT_BLOCK_ACTIONS.BOOK_BLOCK_TAPPED,
              shiftBlockId,
              facilityId,
              location: currentRouterUrl,
            });
            bookShiftBlockState.openModal();
          }
        }}
      >
        Book This Block
      </Button>

      <BookShiftBlockDialog
        modalState={bookShiftBlockState}
        shifts={shifts}
        shiftBlockId={shiftBlockId}
        agentId={agentId}
        facilityId={facilityId}
        onConfirmBooking={onBookingShiftBlock}
      />
    </>
  );
}
